import { lazy, Suspense } from 'react'
import { CircleVideo } from './CampaignVideo'
import { campaignVideoUrl } from './campaignVideoUrl'
import FloatingWhatsAppButton from './FloatingWhatsAppButton'
import OficinaWorld from './OficinaWorld'
import PageOpening from './PageOpening'
import SiteHeader from './SiteHeader'
import RogerioSpeech from './RogerioSpeech'
import SiteFooter from './SiteFooter'
import { ActionLink, PaperCard, SectionHeading, ThreadDivider, WorkshopGlyph } from './ui/Workshop' // termo-ok: nome técnico do arquivo de componentes, não categoria da oferta
import { acompanhamentoPath, bookingLabel, conversaPath, individualBookingUrl, potencialPath } from './siteDestinations'
import { faqItems } from './seo/structuredData'
import './landing-illustrated.css'

const DesignSystem = import.meta.env.DEV ? lazy(() => import('./DesignSystem')) : null

/** A ficha do formato, em números. */
const ficha = [
  ['10+', 'anos ensinando adultos'],
  ['30 min', 'a primeira conversa, gratuita'],
  ['9 h', 'de acompanhamento pago'],
  ['1 a 2 h', 'por semana, por videochamada'],
] as const

/** Os passos do acompanhamento, na faixa que corre. */
const faixa = ['conversar', 'escolher uma tarefa', 'fazer junto', 'tentar sozinho', 'voltar', 'ideias novas', 'seguir sozinho']

/** Os três momentos que todo acompanhamento percorre. O primeiro é a conversa gratuita. */
const momentos = [
  { tone: 'sage', glyph: 'person', label: '01 · Conversa', title: 'Você me conta o que faz.', text: 'Eu mostro onde a IA entra no seu caso e como seria o caminho. Você sai com o primeiro passo, para tentar sozinho.' },
  { tone: 'sky', glyph: 'notes', label: '02 · A primeira tarefa', title: 'A gente faz junto.', text: 'Uma coisa do seu dia a dia, feita com a IA. Você vê como pedir, em português, e como conferir o que saiu. O que a gente faz fica no seu computador.' },
  { tone: 'peach', glyph: 'thread', label: '03 · Seguir sozinho', title: 'Você continua. Eu acompanho.', text: 'Entre um encontro e outro, você tenta. No seguinte, a gente parte de onde você chegou, com ideias novas. No fim, a mesa é sua.' },
] as const

/** As tarefas de quem trabalha no computador. */
const tarefas = [
  ['E-mails e mensagens', 'o que você responde todo dia'],
  ['Planilhas', 'o que você organiza e confere'],
  ['Documentos e relatórios', 'o que você escreve e revisa'],
  ['Atendimento', 'o que você explica para clientes'],
  ['Áudios e reuniões', 'o que vira texto depois'],
  ['A tarefa que se repete', 'toda semana, do mesmo jeito'],
] as const

/** O que está incluído no acompanhamento. */
const incluido = [
  'Encontros individuais, online, por videochamada',
  'Na sua tela, nas tarefas que você já faz',
  '9 horas de acompanhamento pago, de 1 a 2 horas por semana, além da conversa gratuita',
  'O que a gente faz fica guardado no seu computador, do seu jeito, para abrir de novo e continuar',
  'Entre um encontro e outro, você continua. No seguinte, a gente parte de onde você chegou',
  'Ideias novas conforme o que você trouxer: imagem, vídeo, uma tarefa repetida que passa a andar sozinha',
]

/** Os 30 minutos da primeira conversa, como acontecem. */
const minutos = [
  'Você me conta o que faz no computador.',
  'Eu mostro onde a IA entra no seu caso e como seria o caminho.',
  'Você sai com o primeiro passo, para tentar sozinho.',
]

function BookingLink({ variant }: { variant?: 'primary' | 'light' }) {
  return <ActionLink variant={variant} href={individualBookingUrl} target="_blank" rel="noopener noreferrer">{bookingLabel}</ActionLink>
}

/** O vídeo da polaroid é o mesmo do protótipo aprovado; a variável de ambiente pode trocar por uma chave versionada. */
export const apresentacaoVideoUrl = 'https://videos.oficina-ia.app/50-mais/apresentacao.mp4'
const apresentacaoVideoLabel = 'Assistir ao vídeo de apresentação do Arthur, 1 minuto e 30'

/**
 * A landing da primeira conversa: a abertura sobre a paisagem e uma folha de
 * papel com o resto da história. O Rogério apresenta e nunca pede nada.
 */
export default function App({ video = campaignVideoUrl() ?? apresentacaoVideoUrl }: { video?: string }) {
  const params = new URLSearchParams(import.meta.env.DEV && typeof window !== 'undefined' ? window.location.search : '')
  if (DesignSystem && params.get('design') === 'oficina') return <Suspense fallback={null}><DesignSystem /></Suspense>

  return <div className="landing" id="topo">
    <OficinaWorld />
    <a className="skip-link" href="#conteudo">Pular para o conteúdo</a>
    <SiteHeader homeHref="#topo" />
    <main id="conteudo" tabIndex={-1}>
      <PageOpening
        eyebrow="Acompanhamento individual · online"
        title={<>Aprenda a usar IA no seu computador, <em>nas tarefas que você já faz.</em></>}
        intro="Você me conta o que faz no computador. Eu mostro onde a IA entra no seu caso. A gente faz junto até você seguir sozinho."
        rogerio={{ pose: 'curioso', text: 'Pode entrar. A conversa é com o Arthur.' }}
        facts={ficha}
        flow={faixa}
        flowLabel="Os passos do acompanhamento"
        secondary={{ href: '#como-funciona', label: 'Como funciona' }}
      />

      <div className="of-folha">
        <section className="of-secao" id="como-funciona">
          <div className="of-wrap">
            <SectionHeading eyebrow="Como funciona" title={<>Aprenda fazendo.<br />No seu ritmo.</>} centered>Online, na sua tela, sobre as suas tarefas. Todo mundo que acompanho passa por estes três momentos. O tamanho de cada um é seu.</SectionHeading>
            <div className="of-passos-wrap">
              <ThreadDivider />
              <ol className="of-passos">
                {momentos.map((momento, index) => <li key={momento.title}>
                  <PaperCard tone={momento.tone} className="of-passo">
                    <div className="of-passo__topo"><WorkshopGlyph kind={momento.glyph} /><span className="of-passo__numero">{momento.label}</span></div>
                    <h3>{momento.title}</h3>
                    <p>{momento.text}</p>
                    {index === 0 && <span className="of-passo__gratis">Até aqui, gratuito</span>}
                  </PaperCard>
                </li>)}
              </ol>
            </div>
            <p className="of-frase">A frase que eu mais ouço na segunda conversa: <em>eu consegui, e agora tenho um monte de ideias.</em></p>
          </div>
        </section>

        <section className="of-secao of-secao--creme" id="para-quem">
          <div className="of-wrap">
            <SectionHeading eyebrow="Para quem" title={<>Para quem trabalha<br />no computador.</>}>Qualquer área. O que muda é a tarefa que a gente pega primeiro.</SectionHeading>
            <ul className="of-tarefas">
              {tarefas.map(([title, text]) => <li key={title}>{title} <small>{text}</small></li>)}
            </ul>
            <p className="of-quem__fora">Se a sua tarefa está fora dessa lista, ela é a primeira coisa que a gente conversa.</p>
            <p><a href={potencialPath}>Veja o que mais dá para fazer com IA →</a></p>
          </div>
        </section>

        <section className="of-secao" id="acompanhamento">
          <div className="of-wrap of-plano">
            <SectionHeading eyebrow="Se continuar" title="O acompanhamento.">Encontros individuais comigo, na sua tela, sobre as suas tarefas. O primeiro passo fica com você. Continuar comigo é uma escolha sua.</SectionHeading>
            <PaperCard className="of-cartao">
              <div className="of-cartao__topo"><WorkshopGlyph kind="person" /><span className="of-badge">Um olhar para o seu caso</span></div>
              <p className="eyebrow"><span />O que está incluído</p>
              <h3>Acompanhamento individual</h3>
              <ul className="of-lista">{incluido.map(item => <li key={item}>{item}</li>)}</ul>
              <div className="of-cartao__pe">
                <p>O valor e a distribuição das horas a gente combina na primeira conversa.</p>
                <a href={acompanhamentoPath}>Veja tudo o que está incluído →</a>
                <BookingLink />
              </div>
            </PaperCard>
          </div>
        </section>

        <section className="of-secao of-secao--floresta" id="conversa" aria-labelledby="conversa-title">
          <div className="of-wrap of-conversa">
            <div>
              <p className="eyebrow"><span />A primeira conversa</p>
              <h2 id="conversa-title">Como são os 30 minutos.</h2>
              <p className="of-conversa__escolha">Online, no horário que você escolher na minha agenda. Você sai com o primeiro passo, para tentar sozinho.</p>
              <p className="of-conversa__gratis">A primeira conversa é gratuita.</p>
              <p><a href={conversaPath}>Saiba o que levar e com o que você sai →</a></p>
              <div className="of-acoes"><BookingLink variant="light" /></div>
            </div>
            <ol className="of-minutos" aria-label="Os 30 minutos da primeira conversa">
              {minutos.map(line => <li key={line}>{line}</li>)}
            </ol>
          </div>
        </section>

        <section className="of-secao" id="arthur">
          <div className="of-wrap of-sobre">
            <figure className="of-polaroid">
              <span className="of-fita" aria-hidden="true" />
              <CircleVideo src={video} label={apresentacaoVideoLabel} />
              <figcaption>Arthur, em um minuto e meio. <span aria-hidden="true">✳</span></figcaption>
            </figure>
            <div>
              <SectionHeading eyebrow="Tem gente de verdade por aqui" title={<>Prazer,<br />eu sou o Arthur.</>} />
              <p className="of-sobre__texto">Ensino idiomas para adultos há mais de dez anos. Criei a Oficina IA para ajudar pessoas de qualquer área a usar IA em tarefas reais e continuar por conta própria.</p>
              <p className="of-sobre__texto">A primeira conversa e o acompanhamento são comigo.</p>
              <RogerioSpeech pose="concluido" layout="beside">Ah! e o Arthur fez esse site todinho com IA</RogerioSpeech>
            </div>
          </div>
        </section>

        <section className="of-secao of-secao--creme" id="duvidas" aria-labelledby="duvidas-title">
          <div className="of-wrap of-duvidas">
            <div className="of-duvidas__intro">
              <p className="eyebrow"><span />Ainda tem alguma ponta solta?</p>
              <p className="of-duvidas__titulo" id="duvidas-title">É normal ter<br />algumas dúvidas.</p>
              <p className="of-duvidas__nota">O que me perguntam antes de marcar.</p>
            </div>
            <div className="of-duvidas__lista">
              {faqItems.map((item, index) => <details key={item.question} open={index === 0}>
                <summary>{index === 0 ? <h2>{item.question}</h2> : <h3>{item.question}</h3>}</summary>
                <p>{item.answer}</p>
              </details>)}
            </div>
          </div>
        </section>

        <section className="of-fecho" aria-labelledby="fecho-title">
          <div className="of-fecho__arte" aria-hidden="true" />
          <div className="of-fecho__conteudo">
            <p className="eyebrow"><span />Todo começo cabe numa conversa</p>
            <h2 id="fecho-title">Então, o que você faz no computador?</h2>
            <p>O que estava só na sua cabeça passa a existir no seu computador, feito por você. Começa com 30 minutos, online, no horário que você escolher.</p>
            <p className="of-fecho__gratis">A primeira conversa é gratuita.</p>
            <div className="of-acoes"><BookingLink variant="light" /></div>
            <RogerioSpeech pose="concluido" layout="beside">Eu fico por aqui. A conversa é com o Arthur.</RogerioSpeech>
          </div>
        </section>
      </div>
    </main>
    <FloatingWhatsAppButton />
    <SiteFooter homeHref="#topo" />
  </div>
}
